import { useState } from 'react';
import styled from '@emotion/styled';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { Container, Form, Input, Button, Header, Segment, Grid, Message } from 'semantic-ui-react';
import testService from 'src/services/api/testService';

const TEMPLATE_NAME = 'inscripcion_nuevo_discipulo';

const PageContainer = styled.div`
  padding: 20px 10px;
  min-height: 100vh;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);

  @media (min-width: 768px) {
    padding: 40px 20px;
  }

  .page-card {
    max-width: 1000px;
    margin: 0 auto;
    border-radius: 15px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    padding: 15px !important;

    @media (min-width: 768px) {
      padding: 2em !important;
    }
  }

  .page-title {
    color: #128c7e !important;
    margin-bottom: 10px !important;
  }

  .template-name {
    text-align: center;
    color: #666;
    margin-bottom: 24px;
    font-size: 0.95rem;
  }

  .preview-panel {
    background: #e5ddd5;
    border-radius: 12px;
    padding: 20px;
    min-height: 320px;
  }

  .message-bubble {
    background: #fff;
    border-radius: 8px;
    padding: 14px 16px;
    max-width: 100%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
    white-space: pre-wrap;
    line-height: 1.5;
    color: #111;
    font-size: 0.95rem;
  }

  .message-bubble strong {
    font-weight: 700;
  }

  .message-footer {
    margin-top: 8px;
    font-size: 0.75rem;
    color: #667781;
    text-align: right;
  }

  .ui.form label {
    font-weight: 600;
    color: #333;
  }
`;

const buildPreview = ({ nombre, liderNombre, liderTelefono }) => {
  const name = nombre || '{{1}}';
  const leader = liderNombre || '{{2}}';
  const leaderPhone = liderTelefono || '{{3}}';

  return `¡Hola ${name}! ☺️

Bienvenid@ a Comunidad MDE.🎉

Estamos muy felices por tu decisión de seguir a Jesús y unirte al grupo de discipulado liderado por ${leader}

Será un espacio donde podrás conocer más a Jesús, crecer en tu fe, construir amistades y caminar acompañado.

El líder se pondrá en contacto contigo muy pronto para darte más detalles. Si deseas también puedes comunicarte con él al número ${leaderPhone}

¡Estás en Comunidad, estás en Casa! 🏠`;
};

const WhatsAppTemplateTest = () => {
  const [phone, setPhone] = useState('');
  const [nombre, setNombre] = useState('');
  const [liderNombre, setLiderNombre] = useState('');
  const [liderTelefono, setLiderTelefono] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phone.trim()) {
      return Swal.fire(
        'Campo requerido',
        'Ingresa el número de teléfono del destinatario.',
        'warning'
      );
    }

    if (!nombre.trim() || !liderNombre.trim() || !liderTelefono.trim()) {
      return Swal.fire(
        'Campos requeridos',
        'Completa el nombre del discípulo, el líder y el teléfono del líder.',
        'warning'
      );
    }

    setLoading(true);

    try {
      const { data } = await testService.whatsappTemplateTest({
        phone: phone.trim(),
        templateName: TEMPLATE_NAME,
        languageCode: 'es_CO',
        bodyParams: [nombre.trim(), liderNombre.trim(), liderTelefono.trim()],
      });

      Swal.fire({
        icon: 'success',
        title: 'Mensaje enviado',
        text: 'La plantilla de WhatsApp fue enviada correctamente.',
        footer: data?.data?.messages?.[0]?.id ? `ID: ${data.data.messages[0].id}` : '',
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.error?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        'No se pudo enviar el mensaje.';

      Swal.fire('Error al enviar', String(errorMessage), 'error');
    } finally {
      setLoading(false);
    }
  };

  const previewText = buildPreview({ nombre, liderNombre, liderTelefono });

  return (
    <PageContainer>
      <Container fluid>
        <Segment className="page-card">
          <Header as="h1" textAlign="center" className="page-title">
            Prueba de WhatsApp
          </Header>
          <p className="template-name">
            Plantilla: <strong>{TEMPLATE_NAME}</strong>
          </p>

          <Grid stackable>
            <Grid.Row>
              <Grid.Column mobile={16} tablet={8} computer={8}>
                <Form onSubmit={handleSubmit}>
                  <Message info size="small">
                    Envía un mensaje de prueba con la plantilla de inscripción de nuevo discípulo.
                  </Message>

                  <Form.Field required>
                    <label>Teléfono destinatario</label>
                    <Input
                      placeholder="Ej: 3012197448"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      fluid
                    />
                  </Form.Field>

                  <Form.Field required>
                    <label>{'{{1}}'} Nombre del discípulo</label>
                    <Input
                      placeholder="Ej: David"
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      fluid
                    />
                  </Form.Field>

                  <Form.Field required>
                    <label>{'{{2}}'} Nombre del líder</label>
                    <Input
                      placeholder="Ej: Juan Pérez"
                      value={liderNombre}
                      onChange={(e) => setLiderNombre(e.target.value)}
                      fluid
                    />
                  </Form.Field>

                  <Form.Field required>
                    <label>{'{{3}}'} Teléfono del líder</label>
                    <Input
                      placeholder="Ej: 3001234567"
                      value={liderTelefono}
                      onChange={(e) => setLiderTelefono(e.target.value)}
                      fluid
                    />
                  </Form.Field>

                  <Button
                    type="submit"
                    color="green"
                    fluid
                    size="large"
                    loading={loading}
                    disabled={loading}
                    icon="whatsapp"
                    content="Enviar mensaje de prueba"
                  />
                </Form>
              </Grid.Column>

              <Grid.Column mobile={16} tablet={8} computer={8}>
                <Header as="h3" style={{ color: '#128c7e', marginBottom: '12px' }}>
                  Vista previa
                </Header>
                <div className="preview-panel">
                  <div className="message-bubble">{previewText}</div>
                  <div className="message-footer">Comunidad MDE</div>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>
      </Container>
    </PageContainer>
  );
};

export default WhatsAppTemplateTest;
